import { iDSConfig } from '@ids/web/tools';
import { replaceAccents } from '@utils/helpers';

/** IDS v2 theme used as fallback whenever a segment is unavailable on the CDN. */
export const DEFAULT_SEGMENT = 'varejo';

/**
 * Maps legacy IDS v1 / Contentstack segment keys to their IDS v2 theme key for
 * brands that were renamed between versions. e.g. v1 `itau_bba` -> v2 `ibba`.
 */
const SEGMENT_V1_TO_V2: Record<string, string> = {
  itau_bba: 'ibba',
};

/**
 * Normalises a Contentstack `segment_group.name` to an IDS theme key
 * (lowercase, no accents, no spaces). e.g. "Personnalité" -> "personnalite".
 */
export function normalizeSegment(name: string): string {
  return replaceAccents(name.trim().toLowerCase()).replace(/\s+/g, '');
}

/**
 * Resolves a Contentstack segment name to the IDS v2 theme key, applying the
 * v1 -> v2 rename map when needed.
 */
export function resolveThemeKey(name: string): string {
  const key = normalizeSegment(name);
  return SEGMENT_V1_TO_V2[key] ?? key;
}

/** Builds the IDS v2 tokens URL (without extension) for a given theme. */
function buildTokensUrl(theme: string): string {
  const { baseUrl, environment, tokensVersion } = iDSConfig().getConfig();
  const version = tokensVersion || 'v2';
  if (baseUrl) {
    return `${baseUrl}/ids/tokens/${version}/${theme}/tokens`;
  }
  const suffix = environment && environment !== 'prod' ? `-${environment}` : '';
  return `https://ids${suffix}.cloud.itau.com.br/cdn/tokens/${version}/${theme}/tokens`;
}

/** Returns true when the theme tokens are available on the IDS CDN. */
async function themeExists(theme: string): Promise<boolean> {
  try {
    const response = await fetch(`${buildTokensUrl(theme)}.json`, {
      method: 'HEAD',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Switches the active IDS v2 theme/segment. Replaces the legacy Angular
 * `IdsSegmentService.changeSegment`; IDS v2 loads the matching brand tokens
 * from the CDN automatically.
 *
 * Segments that no longer exist on the IDS v2 CDN (e.g. `cartao_branco`) would
 * trigger a 404 and break the styles, so the theme is validated first and
 * falls back to `varejo` when unavailable.
 */
export async function changeSegment(name: string): Promise<void> {
  if (!name) return;

  const theme = resolveThemeKey(name);
  const exists = theme === DEFAULT_SEGMENT || (await themeExists(theme));

  iDSConfig().setTheme(exists ? theme : DEFAULT_SEGMENT);
}
